import { useState, useEffect } from 'react';
import { Form, Modal } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { mutate } from 'swr';

interface InvoiceDataType {
    key: React.Key;
    invoiceNo: string;
    invoiceDate: string;
    customerCode: string;
    customerName: string;
    balance: number;
    amountPayment: number;
    invoiceAmount: number;
    transactionTo: string;
    warehouse: string;
    top: string;
    warehouseCode: string
}

interface SalesTopType {
    code: string;
    deskripsi: string;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export const useTransactionInvoiceLogic = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [originalBalance, setOriginalBalance] = useState<number>(0);
    const [isPaidOff, setIsPaidOff] = useState<boolean>(false);
    const [salesTopData, setSalesTopData] = useState<SalesTopType[]>([]);
    const [originalCodes, setOriginalCodes] = useState<{ warehouseCode: string | null, topCode: string | null }>({ warehouseCode: null, topCode: null });
    const [customerInfo, setCustomerInfo] = useState<{ code: string | null, name: string | null }>({ code: null, name: null });
    const { userData } = useAuth();
    
    useEffect(() => {
        const setInitialDateTime = () => {
            const now = new Date();
            const day = String(now.getDate()).padStart(2, '0');
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const year = now.getFullYear();
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const formattedDateTime = `${day}/${month}/${year} ${hours}:${minutes}`;
            form.setFieldsValue({ date_trans: formattedDateTime });
        };
        const fetchSalesTop = async () => {
            try {
                const response = await axios.get<SalesTopType[]>(`${backendUrl}/api/sales-top`);
                setSalesTopData(response.data);
            } catch (error) {
                console.error("Failed to fetch sales TOP data:", error);
            }
        };
        setInitialDateTime();
        fetchSalesTop();
    }, [form]);

    const onChange = (value: string) => {
        console.log(`Selected: ${value}`);
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleRowSelect = async (record: InvoiceDataType) => {
        const topDesc = salesTopData.find(item => item.code === record.top)?.deskripsi || record.top;
        setOriginalCodes({ warehouseCode: record.warehouseCode, topCode: record.top });
        setCustomerInfo({ code: record.customerCode, name: record.customerName });

        try {
            const response = await axios.get<{ newTransId: string }>(`${backendUrl}/api/generate-new-trans-id`);
            const newTransId = response.data.newTransId;
            const nextTransactionTo = (parseInt(record.transactionTo, 10) || 0) + 1;

            form.setFieldsValue({
                id_trans: newTransId,
                invoice_no: record.invoiceNo,
                transaction_to: nextTransactionTo.toString(),
                warehouse: record.warehouse,
                top: topDesc,
                saldo_akhir: new Intl.NumberFormat('id-ID').format(record.balance),
                amount: '',
            });
        } catch (error) {
            console.error("Failed to generate new transaction ID:", error);
        }

        setOriginalBalance(record.balance);
        setIsPaidOff(false);
        handleCancel();
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const numericValue = value.replace(/[^0-9]/g, '');
        const amount = Number(numericValue);

        if (numericValue) {
            const formattedValue = new Intl.NumberFormat('id-ID').format(amount);
            const newBalance = originalBalance - amount;
            const formattedBalance = new Intl.NumberFormat('id-ID').format(newBalance);

            form.setFieldsValue({
                amount: formattedValue,
                saldo_akhir: formattedBalance
            });
            setIsPaidOff(amount === originalBalance && amount !== 0)
        } else {
            form.setFieldsValue({
                amount: '',
                saldo_akhir: new Intl.NumberFormat('id-ID').format(originalBalance)
            });
            setIsPaidOff(false);
        }
    };

    const handlePaidOffChange = (e: CheckboxChangeEvent) => {
        const isChecked = e.target.checked;
        setIsPaidOff(isChecked);

        if (isChecked) {
            const formattedAmount = new Intl.NumberFormat('id-ID').format(originalBalance);
            form.setFieldsValue({
                amount: formattedAmount,
                saldo_akhir: '0'
            });
        } else {
            form.setFieldsValue({
                amount: '',
                saldo_akhir: new Intl.NumberFormat('id-ID').format(originalBalance)
            });
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const amount = values.amount ? parseInt(values.amount.replace(/\./g, ''), 10) : 0;
            const saldo_akhir = values.saldo_akhir ? parseInt(values.saldo_akhir.replace(/\./g, ''), 10) : 0;

            if (!values.invoice_no || !originalCodes.warehouseCode) {
                Modal.error({ title: 'Error', content: 'Silakan pilih invoice terlebih dahulu.' });
                return;
            }
            if (!values.trans_type) {
                Modal.error({ title: 'Error', content: 'Tipe transaksi harus dipilih.' });
                return;
            }
            if (amount <= 0) {
                Modal.error({ title: 'Error', content: 'Jumlah pembayaran harus lebih besar dari nol.' });
                return;
            }

             const payload = {
                id_trans: values.id_trans,
                date_trans: values.date_trans,
                trans_type: values.trans_type,
                invoice_no: values.invoice_no,
                transaction_to: values.transaction_to,
                warehouse: originalCodes.warehouseCode,
                top: originalCodes.topCode,
                amount: amount,
                saldo_akhir: saldo_akhir,
                customer_code: customerInfo.code,
                customer_name: customerInfo.name,
                user_log: userData?.kode_grup1,
                remark: values.remark,
            };

            await axios.post(`${backendUrl}/api/submit-transaction-invoice`, payload);

            Modal.success({
                title: 'Success',
                content: 'Transaksi berhasil disimpan.',
                onOk: () => {
                    form.resetFields();
                    setOriginalBalance(0);
                    setIsPaidOff(false);
                    setOriginalCodes({ warehouseCode: null, topCode: null });
                    setCustomerInfo({ code: null, name: null });
                    const now = new Date();
                    const day = String(now.getDate()).padStart(2, '0');
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const year = now.getFullYear();
                    const hours = String(now.getHours()).padStart(2, '0');
                    const minutes = String(now.getMinutes()).padStart(2, '0');
                    const formattedDateTime = `${day}/${month}/${year} ${hours}:${minutes}`;
                    form.setFieldsValue({ date_trans: formattedDateTime });
                    mutate(`${backendUrl}/api/transaction-invoices-data`);
                }
            });

        } catch (error) {
            console.error('Failed to submit transaction:', error);
            Modal.error({ title: 'Submit Failed', content: 'Gagal menyimpan transaksi. Silakan cek konsol untuk detail.' });
        }
    };

    return {
        transformStyle,
        onChange,
        form,
        isModalVisible,
        isPaidOff,
        showModal,
        handleCancel,
        handleRowSelect,
        handleAmountChange,
        handlePaidOffChange,
        handleSubmit
    };
};